import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import type { NewsStoryThread } from '../../types';

/**
 * The stories this beat is tracking over time — i.e. what it will and won't
 * re-report.
 *
 * This screen exists because the de-duplication is otherwise invisible machinery
 * with opinions. When a user asks "why didn't the earthquake show up today?",
 * the answer is here: the thread, when it was last reported, and the running
 * digest of what has already been said. Closing a thread is the manual override.
 */

const STATUS_STYLE: Record<string, string> = {
  ACTIVE: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300',
  DORMANT: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
  CLOSED: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 line-through',
};

function when(iso?: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : '-';
}

export default function ThreadsPanel({ beatId, reloadKey }: { beatId: number; reloadKey?: number }) {
  const [threads, setThreads] = useState<NewsStoryThread[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setThreads(await api.listNewsThreads(beatId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [beatId]);

  // `reloadKey` moves as a run files stories, so tracked stories appear while
  // you watch rather than after a tab switch.
  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const setStatus = async (thread: NewsStoryThread, status: string) => {
    try {
      const next = await api.updateNewsThread(thread.id, { status });
      setThreads((rows) => rows.map((r) => (r.id === next.id ? next : r)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const needle = query.trim().toLowerCase();
  const visible = needle
    ? threads.filter((t) => t.title.toLowerCase().includes(needle))
    : threads;

  if (loading) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>;
  }

  return (
    <div className="space-y-3 max-w-4xl">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Stories this beat has reported and is still watching. A tracked story is
        only reported again when something new happens - or when your repeat
        setting allows a restatement.
      </p>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {threads.length > 8 && (
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter tracked stories…"
          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-3 py-1.5 text-sm"
        />
      )}

      {threads.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center text-sm text-gray-600 dark:text-gray-300">
          Nothing tracked yet. Threads appear once this beat reports its first
          story.
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((thread) => (
            <div
              key={thread.id}
              className="rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <button
                onClick={() => setOpenId(openId === thread.id ? null : thread.id)}
                className="w-full text-left px-4 py-3 flex items-start gap-3"
              >
                <span
                  className={`mt-1 shrink-0 transition-transform ${
                    openId === thread.id ? 'rotate-90' : ''
                  }`}
                  aria-hidden
                >
                  ›
                </span>
                <span className="flex-1 min-w-0">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {thread.title}
                    </span>
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded ${
                        STATUS_STYLE[thread.status] ?? ''
                      }`}
                    >
                      {thread.status.toLowerCase()}
                    </span>
                  </span>
                  <span className="block mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    reported {thread.report_count}× · last{' '}
                    {when(thread.last_reported_at)} · {thread.article_count}{' '}
                    articles
                  </span>
                </span>
              </button>

              {openId === thread.id && (
                <div className="px-4 pb-4 pl-11 space-y-3">
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Already reported
                    </h4>
                    <pre className="mt-1 whitespace-pre-wrap font-sans text-sm text-gray-700 dark:text-gray-300">
                      {thread.digest || '(nothing yet)'}
                    </pre>
                  </div>
                  <div className="flex gap-3 text-xs">
                    {thread.status !== 'CLOSED' ? (
                      <button
                        onClick={() => setStatus(thread, 'CLOSED')}
                        className="text-red-600 dark:text-red-400 hover:underline"
                      >
                        Close this story
                      </button>
                    ) : (
                      <button
                        onClick={() => setStatus(thread, 'ACTIVE')}
                        className="text-indigo-600 dark:text-indigo-400 hover:underline"
                      >
                        Reopen
                      </button>
                    )}
                    <span className="text-gray-400 dark:text-gray-500">
                      Closing stops it matching future articles, so a
                      similar-sounding new story is reported fresh.
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
